import { AnalyticsTag, AnalyticsTagOperator } from 'app/services/chatBot';
import React, { useMemo } from 'react';

import AnalyticsContext, { NO_OP_ANALYTICS_CONTEXT_VALUE } from './AnalyticsContext';
import type { TagEventCaptureHandler } from './types';
import useEventCaptureDecorators from './useEventCaptureDecorators';

const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  const { withActiveScreen, withTopicSource, withTopicSourceAndActiveScreen } = useEventCaptureDecorators();

  const contextValue = useMemo(() => {
    const analytics = window?.ostro?.analytics;

    if (analytics) {
      const captureEventWithActiveScreen = withActiveScreen(analytics.captureEvent);

      const captureAnalyticsTagEvent: TagEventCaptureHandler = (
        tags: AnalyticsTag[],
        { eventValue, eventType }: { eventValue: string; eventType: string }
      ) => {
        tags.forEach((tag) => {
          const { name, posthog_event_config, tag_pattern_id } = tag;
          const { properties, type } = posthog_event_config;

          if (type !== eventType) return;

          const eventExtraProperties: { [key: string]: string } = {};

          const shouldCapture = properties.some((property) => {
            const { operator, value } = property;

            switch (operator) {
              case AnalyticsTagOperator.EXACT:
                return value === eventValue;
              case AnalyticsTagOperator.IS_SET:
                if (eventValue) {
                  eventExtraProperties.value = eventValue;
                  return true;
                }
                return false;
              case AnalyticsTagOperator.ANY:
                return true;
              default:
                return false;
            }
          });

          if (shouldCapture) {
            captureEventWithActiveScreen(name, {
              ...eventExtraProperties,
              tag_pattern_id,
            });
          }
        });
      };

      return {
        addPersistentParams: analytics.addPersistentParams,
        captureAnalyticsTagEvent,
        captureEvent: analytics.captureEvent,
        captureEventWithActiveScreen,
        captureEventWithTopicSource: withTopicSource(analytics.captureEvent),
        captureEventWithTopicSourceAndActiveScreen: withTopicSourceAndActiveScreen(analytics.captureEvent),
      };
    } else {
      return NO_OP_ANALYTICS_CONTEXT_VALUE;
    }
  }, [withActiveScreen, withTopicSource, withTopicSourceAndActiveScreen]);

  return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
};

export default AnalyticsProvider;
